import { useState, useCallback } from "react";
import { AppSidebar } from "@/components/AppSidebar";
import { SidebarProvider, SidebarInset } from "@/components/ui/sidebar";
import { FixedPageHeader } from "@/components/ui/fixed-page-header";
import { PAGE_HEADERS } from "@/constants/page-headers";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { usePipelineData } from "@/hooks/use-pipeline-data";
import { Loader2, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const YEARS = ["2026", "2027", "2028"];

interface EditableCellProps {
  value: number | null;
  onSave: (value: number | null) => void;
}

const EditableCell = ({ value, onSave }: EditableCellProps) => {
  const [localValue, setLocalValue] = useState(value?.toString() ?? "");
  const [isDirty, setIsDirty] = useState(false);

  const handleBlur = useCallback(() => {
    if (!isDirty) return;
    const parsed = localValue.trim() === "" ? null : parseInt(localValue, 10);
    if (localValue.trim() !== "" && isNaN(parsed!)) return;
    onSave(parsed);
    setIsDirty(false);
  }, [localValue, isDirty, onSave]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      (e.target as HTMLInputElement).blur();
    }
  };

  return (
    <Input
      type="number"
      value={localValue}
      onChange={(e) => {
        setLocalValue(e.target.value);
        setIsDirty(true);
      }}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      className="h-8 w-full text-center text-sm bg-input-background"
      placeholder="-"
    />
  );
};

const PipelineGrid = ({ year }: { year: number }) => {
  const { data, isLoading, upsertMutation } = usePipelineData(year);
  const [expandedPotentialSales, setExpandedPotentialSales] = useState(false);
  const [expandedCumulatives, setExpandedCumulatives] = useState(false);
  const [expandedSales, setExpandedSales] = useState(false);
  const handleSave = useCallback(
    (month: number, field: "production" | "sales_target" | "planned_deliveries", value: number | null) => {
      upsertMutation.mutate(
        { month, field, value },
        {
          onSuccess: () => toast.success("Saved"),
          onError: () => toast.error("Failed to save"),
        }
      );
    },
    [upsertMutation]
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const manualData = data?.manualData ?? {};
  const stock = data?.stock ?? 0;
  const ordersByMonth = data?.ordersByMonth ?? {};
  const outstandingDevicesByMonth = data?.outstandingDevicesByMonth ?? {};
  const deliveredByMonth = data?.deliveredByMonth ?? {};
  const salesByMonth = data?.salesByMonth ?? {};
  const salesByOrg = data?.salesByOrg ?? [];
  const opportunitiesByMonth = data?.opportunitiesByMonth ?? {};
  const proposalsByMonth = data?.proposalsByMonth ?? {};
  const receivedByMonth = data?.receivedByMonth ?? {};
  const potentialSalesByOrg = data?.potentialSalesByOrg ?? [];

  return (
    <Card>
      <CardContent className="p-0 overflow-x-auto">
        <table className="w-full min-w-[960px] text-sm">
          <thead>
            <tr className="border-b border-border bg-base-beige/50">
              <th className="text-left p-3 font-semibold text-foreground w-[140px] sticky left-0 bg-base-beige/50 z-10">
                Category
              </th>
              {MONTHS.map((m) => (
                <th
                  key={m}
                  className="text-center p-2 font-medium text-foreground/70 min-w-[70px]"
                >
                  {m}
                </th>
              ))}
              <th className="text-center p-2 font-semibold text-foreground min-w-[70px] border-l border-border bg-base-beige/70">
                Total
              </th>
            </tr>
          </thead>
          <tbody>
            {/* ═══ Stock & Production Section ═══ */}
            <tr className="bg-base-beige border-t border-border">
              <td
                colSpan={14}
                className="p-2 px-3 font-semibold text-sm uppercase tracking-wider text-foreground"
              >
                Stock & Production
              </td>
            </tr>

            {/* Stock Row */}
            <tr className="border-b border-border/50">
              <td className="p-3 font-medium text-foreground sticky left-0 bg-card z-10">
                Stock
              </td>
              {MONTHS.map((_, i) => (
                <td key={i} className="text-center p-2 text-foreground">
                  {stock}
                </td>
              ))}
              <td className="text-center p-2 text-muted-foreground border-l border-border bg-base-beige/20">—</td>
            </tr>

            {/* Planned Production Row (editable) */}
            <tr className="border-b border-border/50 bg-base-beige/20">
              <td className="p-3 font-medium text-foreground sticky left-0 bg-base-beige/20 z-10">
                Planned Production
              </td>
              {MONTHS.map((_, i) => {
                const month = i + 1;
                return (
                  <td key={i} className="p-1">
                    <EditableCell
                      value={manualData[month]?.production ?? null}
                      onSave={(v) => handleSave(month, "production", v)}
                    />
                  </td>
                );
              })}
              <td className="text-center p-2 font-semibold text-foreground border-l border-border bg-base-beige/20">
                {Array.from({ length: 12 }, (_, i) => manualData[i + 1]?.production ?? 0).reduce((a, b) => a + b, 0)}
              </td>
            </tr>

            {/* Received into Stock Row */}
            <tr className="border-b border-border/50">
              <td className="p-3 font-medium text-foreground sticky left-0 bg-card z-10">
                Received into Stock
              </td>
              {MONTHS.map((_, i) => (
                <td key={i} className="text-center p-2 text-foreground">
                  {receivedByMonth[i + 1] || 0}
                </td>
              ))}
              <td className="text-center p-2 font-semibold text-foreground border-l border-border bg-base-beige/20">
                {Array.from({ length: 12 }, (_, i) => receivedByMonth[i + 1] || 0).reduce((a, b) => a + b, 0)}
              </td>
            </tr>

            {/* Total Stock Row */}
            <tr className="border-b border-border/50 border-t border-border bg-base-beige/60">
              <td className="p-3 font-semibold text-foreground sticky left-0 bg-base-beige/60 z-10">
                Total Stock
              </td>
              {MONTHS.map((_, i) => {
                let cumulative = stock;
                for (let m = 1; m <= i + 1; m++) {
                  cumulative += receivedByMonth[m] || 0;
                }
                return (
                  <td key={i} className="text-center p-2 font-semibold text-foreground">
                    {cumulative}
                  </td>
                );
              })}
              <td className="text-center p-2 text-muted-foreground border-l border-border bg-base-beige/60">—</td>
            </tr>

            {/* Spacer */}
            <tr>
              <td colSpan={14} className="h-4" />
            </tr>

            {/* ═══ Sales Targets Section ═══ */}
            <tr className="bg-teal-extraLight border-t border-border">
              <td
                colSpan={14}
                className="p-2 px-3 font-semibold text-sm uppercase tracking-wider text-teal-dark"
              >
                Sales Targets
              </td>
            </tr>

            {/* Target Row (editable) */}
            <tr className="border-b border-border/50">
              <td className="p-3 font-medium text-foreground sticky left-0 bg-card z-10">
                Target
              </td>
              {MONTHS.map((_, i) => {
                const month = i + 1;
                return (
                  <td key={i} className="p-1">
                    <EditableCell
                      value={manualData[month]?.sales_target ?? null}
                      onSave={(v) => handleSave(month, "sales_target", v)}
                    />
                  </td>
                );
              })}
              <td className="text-center p-2 font-semibold text-foreground border-l border-border bg-teal-extraLight/40">
                {Array.from({ length: 12 }, (_, i) => manualData[i + 1]?.sales_target ?? 0).reduce((a, b) => a + b, 0)}
              </td>
            </tr>

            {/* Sales Row (clickable to expand org breakdown) */}
            <tr
              className="border-b border-border/50 bg-teal-extraLight/20 cursor-pointer hover:bg-teal-extraLight/40 transition-colors"
              onClick={() => setExpandedSales((prev) => !prev)}
            >
              <td className="p-3 font-medium text-foreground sticky left-0 bg-teal-extraLight/20 z-10 flex items-center gap-1">
                {expandedSales ? (
                  <ChevronDown className="h-4 w-4 inline-block" />
                ) : (
                  <ChevronRight className="h-4 w-4 inline-block" />
                )}
                Sales
              </td>
              {MONTHS.map((_, i) => (
                <td key={i} className="text-center p-2 text-foreground">
                  {salesByMonth[i + 1] || 0}
                </td>
              ))}
              <td className="text-center p-2 font-semibold text-foreground border-l border-border bg-teal-extraLight/40">
                {Array.from({ length: 12 }, (_, i) => salesByMonth[i + 1] || 0).reduce((a, b) => a + b, 0)}
              </td>
            </tr>

            {/* Expanded sales by org rows */}
            {expandedSales &&
              (salesByOrg.length > 0 ? (
                salesByOrg.map((org, idx) => (
                  <tr
                    key={org.orgId}
                    className={`border-b border-border/50 ${idx % 2 === 0 ? "bg-teal-extraLight/10" : ""}`}
                  >
                    <td className={`p-3 pl-8 font-medium text-foreground sticky left-0 z-10 truncate max-w-[140px] ${idx % 2 === 0 ? "bg-teal-extraLight/10" : "bg-card"}`}>
                      {org.orgName}
                    </td>
                    {MONTHS.map((_, i) => (
                      <td key={i} className="text-center p-2 text-foreground">
                        {org.monthlyData[i + 1] || 0}
                      </td>
                    ))}
                    <td className="text-center p-2 font-semibold text-foreground border-l border-border bg-teal-extraLight/10">
                      {Object.values(org.monthlyData).reduce((s, v) => s + v, 0)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr className="border-b border-border/50">
                  <td
                    colSpan={14}
                    className="p-3 text-center text-muted-foreground italic"
                  >
                    No sales data for {year}
                  </td>
                </tr>
              ))}

            {/* Variance Row - expandable toggle */}
            <tr
              className="border-b border-border/50 bg-teal-extraLight/40 cursor-pointer hover:bg-teal-extraLight/60 transition-colors"
              onClick={() => setExpandedCumulatives((prev) => !prev)}
            >
              <td className="p-3 font-medium text-foreground sticky left-0 bg-teal-extraLight/40 z-10 flex items-center gap-1">
                {expandedCumulatives ? (
                  <ChevronDown className="h-4 w-4 inline-block" />
                ) : (
                  <ChevronRight className="h-4 w-4 inline-block" />
                )}
                Variance
              </td>
              {MONTHS.map((_, i) => {
                const month = i + 1;
                const now = new Date();
                const shouldShow = year < now.getFullYear() || (year === now.getFullYear() && month <= now.getMonth() + 1);
                if (!shouldShow) {
                  return <td key={i} className="text-center p-2 text-muted-foreground">—</td>;
                }
                const target = manualData[month]?.sales_target ?? 0;
                const actual = salesByMonth[month] || 0;
                const variance = target - actual;
                return (
                  <td key={i} className={`text-center p-2 font-medium ${variance > 0 ? 'text-destructive' : variance < 0 ? 'text-green-600' : 'text-foreground'}`}>
                    {variance > 0 ? `-${variance}` : variance < 0 ? `+${Math.abs(variance)}` : '0'}
                  </td>
                );
              })}
              <td className="text-center p-2 font-semibold border-l border-border bg-teal-extraLight/40">
                {(() => {
                  const now = new Date();
                  const maxMonth = year < now.getFullYear() ? 12 : year === now.getFullYear() ? now.getMonth() + 1 : 0;
                  const totalTarget = Array.from({ length: maxMonth }, (_, i) => manualData[i + 1]?.sales_target ?? 0).reduce((a, b) => a + b, 0);
                  const totalSales = Array.from({ length: maxMonth }, (_, i) => salesByMonth[i + 1] || 0).reduce((a, b) => a + b, 0);
                  const v = totalTarget - totalSales;
                  if (maxMonth === 0) return <span className="text-muted-foreground">—</span>;
                  return <span className={v > 0 ? 'text-destructive' : v < 0 ? 'text-green-600' : 'text-foreground'}>{v > 0 ? `-${v}` : v < 0 ? `+${Math.abs(v)}` : '0'}</span>;
                })()}
              </td>
            </tr>

            {expandedCumulatives && (
              <>
                <tr className="border-b border-border/50 bg-teal-extraLight/60">
                  <td className="p-3 pl-8 font-semibold text-foreground sticky left-0 bg-teal-extraLight/60 z-10">
                    Cumulative Sales
                  </td>
                  {MONTHS.map((_, i) => {
                    let cumulative = 0;
                    for (let m = 1; m <= i + 1; m++) {
                      cumulative += salesByMonth[m] || 0;
                    }
                    return (
                      <td key={i} className="text-center p-2 font-semibold text-foreground">
                        {cumulative}
                      </td>
                    );
                  })}
                  <td className="text-center p-2 text-muted-foreground border-l border-border bg-teal-extraLight/60">—</td>
                </tr>

                <tr className="border-b border-border/50 bg-teal-extraLight/40">
                  <td className="p-3 pl-8 font-medium text-foreground sticky left-0 bg-teal-extraLight/40 z-10">
                    Cumulative Variance
                  </td>
                  {MONTHS.map((_, i) => {
                    const month = i + 1;
                    const now = new Date();
                    const shouldShow = year < now.getFullYear() || (year === now.getFullYear() && month <= now.getMonth() + 1);
                    if (!shouldShow) {
                      return <td key={i} className="text-center p-2 text-muted-foreground">—</td>;
                    }
                    let cumulativeTarget = 0;
                    let cumulativeSales = 0;
                    for (let m = 1; m <= month; m++) {
                      cumulativeTarget += manualData[m]?.sales_target ?? 0;
                      cumulativeSales += salesByMonth[m] || 0;
                    }
                    const variance = cumulativeTarget - cumulativeSales;
                    return (
                      <td key={i} className={`text-center p-2 font-medium ${variance > 0 ? 'text-destructive' : variance < 0 ? 'text-green-600' : 'text-foreground'}`}>
                        {variance > 0 ? `-${variance}` : variance < 0 ? `+${Math.abs(variance)}` : '0'}
                      </td>
                    );
                  })}
                  <td className="text-center p-2 text-muted-foreground border-l border-border bg-teal-extraLight/40">—</td>
                </tr>
              </>
            )}

            {/* Spacer */}
            <tr>
              <td colSpan={14} className="h-4" />
            </tr>

            {/* ═══ Delivery Section ═══ */}
            <tr className="bg-lime-extra-light border-t border-border">
              <td
                colSpan={14}
                className="p-2 px-3 font-semibold text-sm uppercase tracking-wider text-teal-dark"
              >
                Delivery
              </td>
            </tr>

            {/* Delivered Row */}
            <tr className="border-b border-border/50 bg-lime-extra-light/40">
              <td className="p-3 font-medium text-foreground sticky left-0 bg-lime-extra-light/40 z-10">
                Delivered
              </td>
              {MONTHS.map((_, i) => (
                <td key={i} className="text-center p-2 text-foreground">
                  {deliveredByMonth[i + 1] || 0}
                </td>
              ))}
              <td className="text-center p-2 font-semibold text-foreground border-l border-border bg-lime-extra-light/40">
                {Array.from({ length: 12 }, (_, i) => deliveredByMonth[i + 1] || 0).reduce((a, b) => a + b, 0)}
              </td>
            </tr>


            {/* Outstanding Devices Row */}
            <tr className="border-b border-border/50 bg-lime-extra-light/40">
              <td className="p-3 font-medium text-foreground sticky left-0 bg-lime-extra-light/40 z-10">
                Outstanding Devices
              </td>
              {MONTHS.map((_, i) => (
                <td key={i} className="text-center p-2 text-foreground">
                  {outstandingDevicesByMonth[i + 1] || 0}
                </td>
              ))}
              <td className="text-center p-2 font-semibold text-foreground border-l border-border bg-lime-extra-light/40">
                {Array.from({ length: 12 }, (_, i) => outstandingDevicesByMonth[i + 1] || 0).reduce((a, b) => a + b, 0)}
              </td>
            </tr>

            {/* Planned Deliveries Row (editable) */}
            <tr className="border-b border-border/50">
              <td className="p-3 font-medium text-foreground sticky left-0 bg-card z-10">
                Planned Deliveries
              </td>
              {MONTHS.map((_, i) => {
                const month = i + 1;
                return (
                  <td key={i} className="p-1">
                    <EditableCell
                      value={manualData[month]?.planned_deliveries ?? null}
                      onSave={(v) => handleSave(month, "planned_deliveries", v)}
                    />
                  </td>
                );
              })}
              <td className="text-center p-2 font-semibold text-foreground border-l border-border bg-lime-extra-light/40">
                {Array.from({ length: 12 }, (_, i) => manualData[i + 1]?.planned_deliveries ?? 0).reduce((a, b) => a + b, 0)}
              </td>
            </tr>

            {/* Spacer */}
            <tr>
              <td colSpan={14} className="h-4" />
            </tr>

            {/* ═══ Opportunities, Proposals & Potential Sales Section ═══ */}
            <tr className="bg-base-beige border-t border-border">
              <td
                colSpan={14}
                className="p-2 px-3 font-semibold text-sm uppercase tracking-wider text-foreground"
              >
                Opportunities, Proposals & Potential Sales
              </td>
            </tr>

            {/* Opportunities Row */}
            <tr className="border-b border-border/50">
              <td className="p-3 font-medium text-foreground sticky left-0 bg-card z-10">
                Opportunities
              </td>
              {MONTHS.map((_, i) => (
                <td key={i} className="text-center p-2 text-foreground">
                  {opportunitiesByMonth[i + 1] || 0}
                </td>
              ))}
              <td className="text-center p-2 font-semibold text-foreground border-l border-border bg-base-beige/20">
                {Array.from({ length: 12 }, (_, i) => opportunitiesByMonth[i + 1] || 0).reduce((a, b) => a + b, 0)}
              </td>
            </tr>

            {/* Proposals Row */}
            <tr className="border-b border-border/50 bg-base-beige/20">
              <td className="p-3 font-medium text-foreground sticky left-0 bg-base-beige/20 z-10">
                Proposals
              </td>
              {MONTHS.map((_, i) => (
                <td key={i} className="text-center p-2 text-foreground">
                  {proposalsByMonth[i + 1] || 0}
                </td>
              ))}
              <td className="text-center p-2 font-semibold text-foreground border-l border-border bg-base-beige/20">
                {Array.from({ length: 12 }, (_, i) => proposalsByMonth[i + 1] || 0).reduce((a, b) => a + b, 0)}
              </td>
            </tr>

            {/* Total Potential Sales Row (clickable to expand) */}
            <tr
              className="border-b border-border/50 border-t border-border bg-base-beige/60 cursor-pointer hover:bg-base-beige/80 transition-colors"
              onClick={() => setExpandedPotentialSales((prev) => !prev)}
            >
              <td className="p-3 font-semibold text-foreground sticky left-0 bg-base-beige/60 z-10 flex items-center gap-1">
                {expandedPotentialSales ? (
                  <ChevronDown className="h-4 w-4 inline-block" />
                ) : (
                  <ChevronRight className="h-4 w-4 inline-block" />
                )}
                Total Potential Sales
              </td>
              {MONTHS.map((_, i) => {
                const monthTotal = potentialSalesByOrg.reduce(
                  (sum, org) => sum + (org.monthlyData[i + 1] || 0),
                  0
                );
                return (
                  <td key={i} className="text-center p-2 font-semibold text-foreground">
                    {monthTotal}
                  </td>
                );
              })}
              <td className="text-center p-2 font-semibold text-foreground border-l border-border bg-base-beige/60">
                {potentialSalesByOrg.reduce(
                  (sum, org) =>
                    sum +
                    Object.values(org.monthlyData).reduce((s, v) => s + v, 0),
                  0
                )}
              </td>
            </tr>

            {/* Expanded per-org rows */}
            {expandedPotentialSales &&
              (potentialSalesByOrg.length > 0 ? (
                potentialSalesByOrg.map((org, idx) => (
                  <tr
                    key={org.orgId}
                    className={`border-b border-border/50 ${idx % 2 === 0 ? "bg-base-beige/20" : ""}`}
                  >
                    <td className={`p-3 pl-8 font-medium text-foreground sticky left-0 z-10 truncate max-w-[140px] ${idx % 2 === 0 ? "bg-base-beige/20" : "bg-card"}`}>
                      {org.orgName}
                    </td>
                    {MONTHS.map((_, i) => (
                      <td key={i} className="text-center p-2 text-foreground">
                        {org.monthlyData[i + 1] || 0}
                      </td>
                    ))}
                    <td className="text-center p-2 font-semibold text-foreground border-l border-border bg-base-beige/20">
                      {Object.values(org.monthlyData).reduce((s, v) => s + v, 0)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr className="border-b border-border/50">
                  <td
                    colSpan={14}
                    className="p-3 text-center text-muted-foreground italic"
                  >
                    No questionnaire data for {year}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
};

export default function CrmPipeline() {
  const [selectedYear, setSelectedYear] = useState("2026");

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-background">
        <AppSidebar />
        <SidebarInset>
          <FixedPageHeader {...PAGE_HEADERS.crmPipeline} />
          <div
            className="px-4 md:px-8 py-6 md:py-8 space-y-6"
            style={{ paddingTop: "8.5rem" }}
          >
            <Tabs value={selectedYear} onValueChange={setSelectedYear}>
              <TabsList>
                {YEARS.map((y) => (
                  <TabsTrigger key={y} value={y}>
                    {y}
                  </TabsTrigger>
                ))}
              </TabsList>

              {YEARS.map((y) => (
                <TabsContent key={y} value={y}>
                  <PipelineGrid year={parseInt(y, 10)} />
                </TabsContent>
              ))}
            </Tabs>
          </div>
        </SidebarInset>
      </div>
    </SidebarProvider>
  );
}
